import React, { useState, useEffect } from 'react';
import { Tag, X, Plus } from 'lucide-react';
import apiService from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';

const TagSelector = ({ 
  selectedTags = [], 
  onTagsChange, 
  placeholder = "Selecionar tags...",
  disabled = false,
  allowCreate = true 
}) => {
  const { isDarkMode } = useTheme();
  const [availableTags, setAvailableTags] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);

  // Carregar tags disponíveis
  useEffect(() => {
    loadAvailableTags();
  }, []);

  const loadAvailableTags = async () => {
    try {
      setLoading(true);
      const response = await apiService.request('/crm/tags');
      
      if (response.success && response.data) {
        setAvailableTags(response.data.availableTags || []);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tag) => {
    if (selectedTags.includes(tag)) {
      // Remover tag
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else {
      // Adicionar tag
      onTagsChange([...selectedTags, tag]);
    }
  };

  const handleCreateTag = () => {
    if (newTag.trim() && !availableTags.includes(newTag.trim()) && !selectedTags.includes(newTag.trim())) {
      const trimmedTag = newTag.trim();
      setAvailableTags([...availableTags, trimmedTag]);
      onTagsChange([...selectedTags, trimmedTag]);
      setNewTag('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateTag();
    }
  };

  const removeTag = (tagToRemove) => {
    onTagsChange(selectedTags.filter(tag => tag !== tagToRemove));
  };

  // Memoizar estilos baseados no tema para evitar recálculos desnecessários
  const tagStyles = {
    container: `flex flex-wrap gap-2 mb-2`,
    tag: `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-800'}`,
    icon: `w-3 h-3 mr-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`,
    removeButton: `ml-1 ${isDarkMode ? 'hover:text-purple-300' : 'hover:text-purple-600'}`
  };

  return (
    <div className="relative">
      {/* Tags selecionadas - renderização otimizada */}
      {selectedTags.length > 0 && (
        <div className={tagStyles.container}>
          {selectedTags.map((tag) => (
            <span key={tag} className={tagStyles.tag}>
              <Tag className={tagStyles.icon} />
              {tag}
              {!disabled && (
                <button
                  onClick={() => removeTag(tag)}
                  className={tagStyles.removeButton}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Seletor de tags */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-3 py-2 text-left border rounded-lg focus:outline-none focus:ring-2 disabled:cursor-not-allowed ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white focus:ring-purple-500 disabled:bg-gray-600' : 'border-gray-300 focus:ring-purple-500 disabled:bg-gray-100'}`}
        >
          <div className="flex items-center justify-between">
            <span className={selectedTags.length === 0 ? `${isDarkMode ? 'text-gray-400' : 'text-gray-500'}` : ''}>
              {selectedTags.length === 0 ? placeholder : `${selectedTags.length} tag(s) selecionada(s)`}
            </span>
            <Tag className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
          </div>
        </button>

        {/* Dropdown - renderização otimizada */}
        {isOpen && !disabled && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {/* Campo para criar nova tag - renderizado apenas quando necessário */}
            {allowCreate && (
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Criar nova tag..."
                    className={`flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white focus:ring-purple-500' : 'border-gray-300 focus:ring-purple-500'}`}
                    autoFocus
                  />
                  <button
                    onClick={handleCreateTag}
                    disabled={!newTag.trim()}
                    className="px-2 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded text-sm"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Lista de tags disponíveis */}
            {loading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Carregando tags...
              </div>
            ) : availableTags.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Nenhuma tag disponível
              </div>
            ) : (
              <div className="py-1">
                {availableTags.map((tag) => {
                  const isSelected = selectedTags.includes(tag);
                  const buttonClass = `w-full px-3 py-2 text-left text-sm flex items-center justify-between ${
                    isSelected 
                      ? isDarkMode 
                        ? 'bg-purple-900/20 text-purple-300' 
                        : 'bg-purple-50 text-purple-700'
                      : isDarkMode 
                        ? 'text-gray-300 hover:bg-gray-700' 
                        : 'text-gray-700 hover:bg-gray-100'
                  }`;
                  
                  return (
                    <button
                      key={tag}
                      onClick={() => handleTagToggle(tag)}
                      className={buttonClass}
                    >
                      <div className="flex items-center">
                        <Tag className={`w-3 h-3 mr-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        {tag}
                      </div>
                      {isSelected && (
                        <div className="w-4 h-4 bg-purple-600 rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Overlay para fechar dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default TagSelector;